import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, ArrowLeft, Clock, Anchor, ExternalLink, SlidersHorizontal, Navigation, Hourglass, ChevronDown, MapPin } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { setSeoMetadata } from '../../lib/seo';
import { resolveMediaUrl } from '../../lib/mediaUrl';
import { locationOptionsFor, matchesLocation } from '../../lib/activityLocations';
import { categoryUrl, needsCategoryRedirect } from '../../lib/activityUrls';
import { useActivityMarketplaceGate, API, trackBookingEvent, newBookingSessionId } from './activityMarketplaceShared';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import ActivityViewToggle from '../../components/activities/ActivityViewToggle';
import ActivityListRow from '../../components/activities/ActivityListRow';
import ActivityCompareTray from '../../components/activities/ActivityCompareTray';
import ActivityCardImage from '../../components/activities/ActivityCardImage';
import { useActivityView } from '../../hooks/useActivityView';
import axios from 'axios';

const norm = (value) => (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// The charter company gets its own link above the description, so the description
// must never repeat it. Legacy imports occasionally still hold a company label
// instead of real prose - in that case show nothing rather than a duplicate.
const cardDescription = (a) => {
  const text = (a.short_description || a.description || '').trim();
  if (!text) return '';
  const n = norm(text);
  if (!n || n === norm(a.seller_name) || n === norm(a.title)) return '';
  if (text.split(/\s+/).length <= 3) return '';
  return text;
};

// Renders the activities within a single category OR a single charter company,
// depending on which slug param is present. Same excursion-card grid look either way.
const ActivityListPage = ({ mode, slugOverride }) => {
  const ready = useActivityMarketplaceGate();
  const { slug: paramSlug } = useParams();
  // slugOverride lets a fixed legacy route (e.g. /water-taxi-st-john) render this same
  // directory component without needing a :slug route param.
  const slug = slugOverride || paramSlug;
  const navigate = useNavigate();
  const [canonicalPath, setCanonicalPath] = useState(
    typeof window !== 'undefined' ? window.location.pathname : '',
  );
  const [activities, setActivities] = useState([]);
  const [entityName, setEntityName] = useState('');
  const [loading, setLoading] = useState(true);
  const [locationFilter, setLocationFilter] = useState(null);
  const [durationFilter, setDurationFilter] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [bookingActivity, setBookingActivity] = useState(null);
  const [view, setView] = useActivityView();
  const [compare, setCompare] = useState([]);   // up to 4 trips being compared

  const toggleCompare = (a) => setCompare((prev) => (
    prev.some((x) => x.id === a.id) ? prev.filter((x) => x.id !== a.id) : prev.length >= 4 ? prev : [...prev, a]
  ));

  useEffect(() => {
    // Brand comes from Developer Settings via setSeoBrand(), so only the page part is set here.
    const label = entityName || (mode === 'company' ? 'Charter Company' : 'Activities');
    setSeoMetadata({
      title: mode === 'company' ? `${label} Tours & Charters` : label,
      description: mode === 'company'
        ? `Book ${label} tours, charters and excursions on St John, U.S. Virgin Islands.`
        : `Browse ${label.toLowerCase()} on St John and book direct with the charter company.`,
      // canonical follows the URL actually being viewed, on the Site URL from Developer Settings
      canonicalPath: canonicalPath || window.location.pathname,
    });
  }, [entityName, mode, canonicalPath]);

  useEffect(() => {
    if (!ready) return;
    setLocationFilter(null);
    setDurationFilter(null);
    const param = mode === 'company' ? { seller_slug: slug } : { category_slug: slug };
    const entityEndpoint = mode === 'company' ? 'sellers' : 'categories';
    Promise.all([
      axios.get(`${API}/api/public/tours-charters/activities`, { params: param }),
      axios.get(`${API}/api/public/tours-charters/${entityEndpoint}`),
    ]).then(([actRes, entRes]) => {
      setActivities(actRes.data || []);
      // the URL may be our own slug or the legacy (indexed) alias
      const match = (entRes.data || []).find(
        (e) => e.slug === slug || e.public_slug === slug || e.legacy_path === slug
          || (e.legacy_slugs || []).includes(slug),
      );
      setEntityName(match?.name || '');
      // A category that owns a legacy (indexed) URL is only ever served on that URL: anybody
      // arriving on /activities/category/<slug> is moved across so there is a single live URL.
      if (mode !== 'company' && match) {
        const preferred = categoryUrl(match);
        setCanonicalPath(preferred);
        if (needsCategoryRedirect(match, window.location.pathname)) {
          navigate(preferred, { replace: true });
        }
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [ready, slug, mode, navigate]);

  // Location options are grouped by island, so an activity whose location mentions
  // St Thomas shows up under a single "St Thomas" choice however it was typed.
  const locationOptions = useMemo(() => locationOptionsFor(activities), [activities]);

  const durationOptions = useMemo(() => {
    const seen = [];
    activities.forEach((a) => { if (a.duration && !seen.includes(a.duration)) seen.push(a.duration); });
    return seen;
  }, [activities]);

  const filteredActivities = activities.filter(
    (a) => matchesLocation(a, locationFilter) && (!durationFilter || a.duration === durationFilter),
  );

  const handleBookNow = (a, e) => {
    e.preventDefault();
    e.stopPropagation();
    const sessionId = newBookingSessionId();
    const baseEvent = { activity_id: a.id, activity_title: a.title, seller_id: a.seller_id, seller_name: a.seller_name, booking_provider: a.booking_provider, page_context: 'list', session_id: sessionId };
    trackBookingEvent({ ...baseEvent, event_type: 'book_now_click' });
    if (a.booking_type === 'external_link' && a.booking_provider === 'fareharbor' && a.effective_fareharbor_shortname) {
      trackBookingEvent({ ...baseEvent, event_type: 'drawer_opened' });
      setBookingActivity({ ...a, _sessionId: sessionId, _baseEvent: baseEvent, _openedAt: Date.now() });
    } else if (a.booking_type === 'external_link' && a.booking_url) {
      trackBookingEvent({ ...baseEvent, event_type: 'external_redirect' });
      window.open(a.booking_url, '_blank', 'noopener,noreferrer');
    } else {
      trackBookingEvent({ ...baseEvent, event_type: 'drawer_opened' });
      setBookingActivity({ ...a, _sessionId: sessionId, _baseEvent: baseEvent, _openedAt: Date.now() });
    }
  };

  const closeBookingDrawer = () => {
    if (bookingActivity?._baseEvent) {
      const duration_seconds = (Date.now() - bookingActivity._openedAt) / 1000;
      trackBookingEvent({ ...bookingActivity._baseEvent, event_type: 'drawer_closed', duration_seconds });
    }
    setBookingActivity(null);
  };

  if (!ready) return <div className="min-h-screen bg-white"><Header /></div>;

  const backHref = mode === 'company' ? '/activities/companies' : '/activities';
  const backLabel = mode === 'company' ? 'All Charter Companies' : 'All Activities';

  return (
    <div className="min-h-screen bg-white" data-testid="activity-list-page">
      <Header />
      <section className="relative overflow-hidden bg-white pt-36 pb-10">
        <div className="absolute inset-0 bg-gradient-to-b from-teal-900/30 via-transparent to-transparent" />
        <div className="relative mx-auto max-w-6xl px-6">
          <Link to={backHref} className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-300 hover:text-teal-700" data-testid="activity-list-back-link">
            <ArrowLeft className="h-4 w-4" /> {backLabel}
          </Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-3">
            <h1 className="text-3xl font-black text-slate-900 sm:text-4xl">{entityName || 'Activities'}</h1>
          </motion.div>
        </div>
      </section>

      {openDropdown && <div className="fixed inset-0 z-10" onClick={() => setOpenDropdown(null)} />}

      {!loading && activities.length > 0 && (
        <div className="flex flex-wrap items-center gap-5 border-b border-gray-200 bg-gray-50 px-6 py-3.5" data-testid="activity-list-filter-bar">
          <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-600">
            <SlidersHorizontal className="h-3.5 w-3.5" /> Filter By:
          </span>

          {locationOptions.length > 0 && (
            <div className="relative z-20">
              <button
                onClick={() => setOpenDropdown(openDropdown === 'location' ? null : 'location')}
                className="flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-gray-900"
                data-testid="activity-filter-location-btn"
              >
                <Navigation className="h-3.5 w-3.5" /> {locationFilter || 'Location'} <ChevronDown className="h-3.5 w-3.5" />
              </button>
              {openDropdown === 'location' && (
                <div className="absolute left-0 top-full mt-2 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-lg" data-testid="activity-filter-location-menu">
                  <button onClick={() => { setLocationFilter(null); setOpenDropdown(null); }} className="block w-full px-4 py-2 text-left text-sm text-gray-600 hover:bg-gray-50" data-testid="activity-filter-location-all">All Locations</button>
                  {locationOptions.map((loc) => (
                    <button key={loc} onClick={() => { setLocationFilter(loc); setOpenDropdown(null); }} className={`block w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${locationFilter === loc ? 'font-semibold text-teal-700' : 'text-gray-600'}`} data-testid={`activity-filter-location-${loc.toLowerCase().replace(/\s+/g, '-')}`}>{loc}</button>
                  ))}
                </div>
              )}
            </div>
          )}

          {durationOptions.length > 0 && (
            <div className="relative z-20">
              <button
                onClick={() => setOpenDropdown(openDropdown === 'duration' ? null : 'duration')}
                className="flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-gray-900"
                data-testid="activity-filter-duration-btn"
              >
                <Hourglass className="h-3.5 w-3.5" /> {durationFilter || 'Duration'} <ChevronDown className="h-3.5 w-3.5" />
              </button>
              {openDropdown === 'duration' && (
                <div className="absolute left-0 top-full mt-2 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-lg" data-testid="activity-filter-duration-menu">
                  <button onClick={() => { setDurationFilter(null); setOpenDropdown(null); }} className="block w-full px-4 py-2 text-left text-sm text-gray-600 hover:bg-gray-50" data-testid="activity-filter-duration-all">All Durations</button>
                  {durationOptions.map((d) => (
                    <button key={d} onClick={() => { setDurationFilter(d); setOpenDropdown(null); }} className={`block w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${durationFilter === d ? 'font-semibold text-teal-700' : 'text-gray-600'}`} data-testid={`activity-filter-duration-${d.toLowerCase().replace(/\s+/g, '-')}`}>{d}</button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="ml-auto flex items-center gap-4">
            <span className="text-sm text-slate-500" data-testid="activity-list-results-count">{filteredActivities.length} results</span>
            <ActivityViewToggle view={view} onChange={setView} />
          </div>
        </div>
      )}

      <section className="mx-auto max-w-6xl bg-white px-6 py-10">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-teal-500" /></div>
        ) : activities.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-200 py-20 text-center text-slate-600" data-testid="activity-list-empty">
            No activities here yet. Check back soon!
          </div>
        ) : filteredActivities.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-200 py-20 text-center text-slate-600" data-testid="activity-list-filter-empty">
            No activities match this filter.
          </div>
        ) : view === 'list' ? (
          <div className="flex flex-col gap-4" data-testid="activity-list-rows">
            {filteredActivities.map((a, i) => (
              <motion.div key={a.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 8) * 0.04 }} data-testid={`public-activity-${a.alias}`}>
                <ActivityListRow
                  activity={a}
                  mode={mode}
                  description={cardDescription(a)}
                  onBook={handleBookNow}
                  compareSelected={compare.some((x) => x.id === a.id)}
                  onToggleCompare={toggleCompare}
                  compareFull={compare.length >= 4}
                />
              </motion.div>
            ))}
          </div>
        ) : (
          <div
            className={`grid gap-6 ${view === 'grid3' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'}`}
            data-testid="activity-list-grid"
            data-view={view}
          >
            {filteredActivities.map((a, i) => (
              <motion.div key={a.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} data-testid={`public-activity-${a.alias}`}>
                <div
                  role="button"
                  tabIndex={0}
                  aria-label={`Book ${a.title}`}
                  onClick={(e) => handleBookNow(a, e)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleBookNow(a, e); }}
                  className="group block cursor-pointer overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm transition hover:shadow-md"
                  data-testid={`activity-card-open-${a.alias}`}
                >
                  <ActivityCardImage activity={a} variant="grid" />
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-gray-900">{a.title}</h3>
                    {/* Charter company - links to that company's own root-level page
                        (/arawak-expeditions), exactly like the legacy site did, where
                        every one of their listings is shown. */}
                    {mode !== 'company' && a.seller_slug && (
                      <Link
                        to={`/${a.seller_slug}`}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-0.5 inline-block text-sm font-semibold text-blue-800 underline-offset-2 hover:text-blue-900 hover:underline"
                        data-testid={`activity-card-seller-link-${a.alias}`}
                      >
                        {a.seller_name}
                      </Link>
                    )}
                    {cardDescription(a) && (
                      <p className="mt-2 line-clamp-3 text-sm text-slate-600" data-testid={`activity-card-description-${a.alias}`}>
                        {cardDescription(a)}
                      </p>
                    )}
                    <button
                      onClick={(e) => handleBookNow(a, e)}
                      className="mt-4 flex w-full items-center justify-center gap-2 rounded-sm bg-blue-800 py-3 text-sm font-bold uppercase tracking-wide text-white transition hover:bg-blue-900"
                      data-testid={`activity-card-book-now-${a.alias}`}
                    >
                      {a.booking_type === 'external_link' && a.booking_provider !== 'fareharbor' && a.booking_url ? <ExternalLink className="h-4 w-4" /> : <Anchor className="h-4 w-4" />} Book Now
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>
      <Footer />

      <ActivityCompareTray
        items={compare}
        onRemove={toggleCompare}
        onClear={() => setCompare([])}
        onBook={handleBookNow}
        describe={cardDescription}
      />

      {bookingActivity && (
        <Sheet open={!!bookingActivity} onOpenChange={(open) => !open && closeBookingDrawer()}>
          <SheetContent side="right" className="w-full sm:max-w-4xl bg-white border-slate-200 text-slate-900 p-0 flex flex-col" data-testid="activity-list-booking-drawer">
            <SheetHeader className="p-4 border-b border-slate-200">
              <SheetTitle className="flex items-center gap-2 text-slate-900">
                <Anchor className="h-4 w-4 text-teal-300" /> Book {bookingActivity.title}
              </SheetTitle>
            </SheetHeader>
            {bookingActivity.effective_fareharbor_shortname ? (
              <iframe
                title={`Book ${bookingActivity.title} on FareHarbor`}
                /* booking_embed_url comes from the API already carrying the FareHarbor
                   asn / asn-ref attribution, so the site is credited for the booking. */
                src={bookingActivity.booking_embed_url
                  || `https://fareharbor.com/embeds/book/${bookingActivity.effective_fareharbor_shortname}/${bookingActivity.fareharbor_item_pk ? `items/${bookingActivity.fareharbor_item_pk}/` : ''}?full-items=yes`}
                className="flex-1 w-full border-0"
                data-testid="activity-list-booking-iframe"
              />
            ) : (
              <div className="flex flex-1 items-center justify-center p-8 text-center text-slate-900/40" data-testid="activity-list-booking-coming-soon">
                <span className="flex items-center gap-2"><Clock className="h-4 w-4" /> Booking for this activity is coming soon.</span>
              </div>
            )}
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
};

export default ActivityListPage;
